/**
 * ServerCard Component
 *
 * Displays a MongoDB server with its health status and databases.
 */

import React from 'react';
import { MongoServer, DatabaseConnection, MongoServerHealthStatus } from '@/services/api/serverApi';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  CheckCircle2,
  AlertCircle,
  XCircle,
  Circle,
  Star,
  Folder,
  RefreshCw,
} from 'lucide-react';

interface ServerCardProps {
  server: MongoServer;
  databases: DatabaseConnection[];
  onTest: (server: MongoServer) => void;
  onEdit: (server: MongoServer) => void;
  onDelete: (server: MongoServer) => void;
  onCreateDatabase: (server: MongoServer) => void;
  isTestingConnection?: boolean;
}

const healthStatusConfig: Record<MongoServerHealthStatus, { color: string; label: string; icon: React.ReactNode }> = {
  healthy: { color: 'bg-green-500', label: 'Healthy', icon: <CheckCircle2 className="h-6 w-6 text-green-500" /> },
  degraded: { color: 'bg-yellow-500', label: 'Degraded', icon: <AlertCircle className="h-6 w-6 text-yellow-500" /> },
  unhealthy: { color: 'bg-red-500', label: 'Unhealthy', icon: <XCircle className="h-6 w-6 text-red-500" /> },
  unknown: { color: 'bg-gray-400', label: 'Unknown', icon: <Circle className="h-6 w-6 text-gray-400" /> },
};

export const ServerCard: React.FC<ServerCardProps> = ({
  server,
  databases,
  onTest,
  onEdit,
  onDelete,
  onCreateDatabase,
  isTestingConnection = false,
}) => {
  const healthConfig = healthStatusConfig[server.health.status];
  const activeDbCount = databases.filter((db) => db.status === 'active').length;

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <span className="flex-shrink-0">{healthConfig.icon}</span>
            <div>
              <h3 className="font-semibold text-lg">{server.name}</h3>
              <p className="text-sm text-muted-foreground">
                {server.host}:{server.port} • {activeDbCount} database{activeDbCount !== 1 ? 's' : ''} • {healthConfig.label}
              </p>
            </div>
          </div>
          <div className={cn('px-2 py-1 rounded text-xs font-medium text-white', healthConfig.color)}>
            {healthConfig.label}
          </div>
        </div>

        {/* Health details */}
        {server.health.lastCheck && (
          <div className="mt-2 text-xs text-muted-foreground">
            Last checked: {new Date(server.health.lastCheck).toLocaleString()}
            {server.health.latencyMs && ` • ${server.health.latencyMs}ms`}
            {server.health.version && ` • v${server.health.version}`}
          </div>
        )}

        {server.health.errorMessage && (
          <div className="mt-2 text-xs text-destructive">
            Error: {server.health.errorMessage}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-3 bg-muted/30 flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onTest(server)}
          disabled={isTestingConnection}
        >
          {isTestingConnection ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin mr-1" />
              Testing...
            </>
          ) : (
            'Test Connection'
          )}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onCreateDatabase(server)}
        >
          + New Database
        </Button>
        <div className="flex-1" />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(server)}
        >
          Edit
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={() => onDelete(server)}
        >
          Delete
        </Button>
      </div>

      {/* Databases */}
      {databases.length > 0 && (
        <div className="p-4 border-t border-border">
          <h4 className="text-sm font-medium mb-2">Databases</h4>
          <div className="space-y-2">
            {databases.map((db) => (
              <div
                key={db.id}
                className="flex items-center justify-between p-2 rounded bg-muted/50"
              >
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1.5 text-sm">
                    {db.isDefault ? (
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    ) : (
                      <Folder className="h-4 w-4 text-muted-foreground" />
                    )}
                    {db.name}
                  </span>
                  {db.isDefault && (
                    <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                      default
                    </span>
                  )}
                </div>
                <span
                  className={cn(
                    'text-xs px-1.5 py-0.5 rounded',
                    db.lastHealthStatus === 'healthy'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : db.lastHealthStatus === 'unhealthy'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                  )}
                >
                  {db.lastHealthStatus || 'unknown'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {databases.length === 0 && (
        <div className="p-4 text-center text-muted-foreground text-sm">
          No databases yet. Click "New Database" to create one.
        </div>
      )}
    </div>
  );
};

export default ServerCard;
